import type { Doc } from "building-types/doc";
import type { DocEntityHandle, HidableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { PlanView, View3D } from "building-types/views/View3D";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docGetSolidFloorSystemFloors } from "../docGetFloors";
import { getPlanViewLevelIds } from "../docViewUtils";
import { getFloorIdForBay, getFloorIdForSplitLine } from "../elements/floor/floorAssembly";
import { instanceIdForComponent } from "./componentInstanceEntityResolve";
import { docEntityHandleHash } from "./core/handleHash";
import { docGetGroup } from "./docGetGroup";
import { docGetGroupImmediateChildren } from "./docGetGroupImmediateChildren";
import { docGetTableEntity } from "./docGetTableEntity";

function floorSystemChildIsHiddenOnPlanView(
  doc: Doc,
  floor: { readonly id: string; readonly levelId?: string },
  view: PlanView | View3D,
): boolean {
  if (docEntityIsHidden(doc, { type: "FLOOR", id: floor.id }, view)) {
    return true;
  }
  if (!floor.levelId) {
    return false;
  }
  return docEntityIsHidden(doc, { type: "LEVEL", id: floor.levelId }, view);
}

export const hidableEntityTypes: { [K in HidableEntityHandle["type"]]: true } = {
  GROUP: true,
  LEVEL: true,
  UNDERLAY: true,
  STAIR_BLOCK: true,
  STAIR_SYSTEM: true,
  REFERENCE_GEOM: true,
  ROOF_SYSTEM: true,
  ROOF: true,
  BLOCK: true,
  ROOM: true,
  MEASUREMENT: true,
  FLOOR: true,
  FLOOR_SYSTEM: true,
  FOUNDATION: true,
  FOUNDATION_SYSTEM: true,
};

export function docEntityIsHidable(doc: Doc, handle: DocEntityHandle): handle is HidableEntityHandle {
  if (handle.type !== "GROUP") {
    return handle.type in hidableEntityTypes;
  }

  const group = docGetGroup(doc, handle);

  if (!group || group.isBuiltIn) {
    return false;
  }

  const children = docGetGroupImmediateChildren(doc, handle);
  return children.every((child) => docEntityIsHidable(doc, child));
}

export function docEntityIsHidden(doc: Doc | null, handle: DocEntityHandle, view?: PlanView | View3D): boolean {
  if (!doc) {
    return false;
  }

  if (view?.entityType === "VIEW_3D") {
    return doc.hidden[docEntityHandleHash(handle)] !== undefined;
  }

  if (handle?.type === "LEVEL" && view) {
    // If the view is a composite level plan view, entity hidden status is based on
    // composite level member IDs.
    const levelIds = getPlanViewLevelIds(doc, view);
    // Check for both doc-native and ephemeral levels by composing each member's ID
    // and comparing with the handle.id (which may be a composed ephemeral level ID)
    return !levelIds.some((member) => {
      const memberComposedId = instanceIdForComponent(member.entityId, member.componentInstanceChain);
      return memberComposedId === handle.id;
    });
  }

  if (handle?.type === "UNDERLAY" && view) {
    // Roof underlays live in doc.roofUnderlays rather than doc.underlays.
    // They are building-wide (not tied to a specific floor), so skip the level-membership
    // check and fall through to the generic doc.hidden check.
    if (doc.roofUnderlays?.[handle.id]) {
      return doc.hidden[docEntityHandleHash(handle)] !== undefined;
    }

    const underlay = doc.underlays[handle.id];
    if (!underlay) {
      return doc.hidden[docEntityHandleHash(handle)] !== undefined;
    }

    // If the view is a composite level plan view, entity hidden status is based on
    // composite level member IDs.
    const levelIds = getPlanViewLevelIds(doc, view);

    // If the underlay is associated with a specific level that is **not** part of the
    // current plan view, treat it as hidden.
    // Note: underlay.levelId is always a doc-native level ID, so we only check doc-native members
    if (
      underlay.levelId &&
      !levelIds.some((member) => member.entityId === underlay.levelId && !member.componentInstanceChain)
    ) {
      return true;
    }

    // Otherwise, defer to the generic hidden-set check below so that the entity can be
    // shown/hidden independently of its level association via `doc.hidden`.
  }

  return doc.hidden[docEntityHandleHash(handle)] !== undefined;
}

export function docEntityOrParentIsHidden(doc: Doc | null, handle: DocEntityHandle, view?: PlanView | View3D): boolean {
  if (!doc) {
    return false;
  }

  switch (handle.type) {
    case "DOC_REF":
    case "LEVEL":
    case "UNDERLAY":
    case "FACADE":
    case "STAIR_BLOCK":
    case "STAIR_SYSTEM":
    case "RAILING":
    case "RAILING_POST":
    case "FREEFORM_WALL":
    case "REFERENCE_GEOM":
    case "FOUNDATION_SYSTEM":
    case "ROOF_SYSTEM":
    case "ROOF_VOLUME": {
      return docEntityIsHidden(doc, handle, view);
    }
    case "FLOOR_SYSTEM": {
      if (docEntityIsHidden(doc, handle, view)) {
        return true;
      }
      // Plan views only show floor systems hosted on visible levels.
      if (!view || view.entityType === "VIEW_3D") {
        return false;
      }
      const childFloors = docGetSolidFloorSystemFloors(doc.floors, handle);
      if (childFloors.length === 0) {
        return false;
      }
      return childFloors.every((floor) => floorSystemChildIsHiddenOnPlanView(doc, floor, view));
    }
    case "BLOCK": {
      const block = doc.blocks[handle.id];
      return (
        docEntityIsHidden(doc, handle, view) ||
        (block && docEntityIsHidden(doc, { type: "LEVEL", id: block.levelId }, view))
      );
    }
    case "ROOM": {
      const room = docGetTableEntity(doc, handle);
      if (!room?.parentId) {
        return false;
      }
      return !!(
        docEntityIsHidden(doc, handle, view) ||
        (room && docEntityOrParentIsHidden(doc, { type: "BLOCK", id: room.parentId }, view))
      );
    }
    case "FLOOR": {
      const floor = doc.floors[handle.id];
      if (!floor || !floor.parentId) {
        return false;
      }
      if (doc.rooms?.[floor.parentId]) {
        return docEntityOrParentIsHidden(doc, { id: floor.parentId, type: "ROOM" }, view);
      }
      if (doc.floorSystems?.[floor.parentId]) {
        // Don't delegate to docEntityOrParentIsHidden(FLOOR_SYSTEM) here — that returns true only when
        // all child floors are hidden, but we need to check this specific floor's level visibility.
        if (
          docEntityIsHidden(doc, handle, view) ||
          docEntityIsHidden(doc, { type: "FLOOR_SYSTEM", id: floor.parentId }, view)
        ) {
          return true;
        }
        if (floor.levelId && docEntityIsHidden(doc, { type: "LEVEL", id: floor.levelId }, view)) {
          return true;
        }
        return false;
      }
      return docEntityIsHidden(doc, handle, view);
    }
    case "FLOOR_FINISH": {
      const floorFinish = doc.floorFinishes[handle.id];
      if (!floorFinish || !floorFinish.parentId) {
        return false;
      }
      return docEntityOrParentIsHidden(doc, { id: floorFinish.parentId, type: "ROOM" }, view);
    }
    case "CEILING": {
      const ceiling = doc.ceilings[handle.id];
      if (!ceiling || !ceiling.parentId) {
        return false;
      }
      return docEntityOrParentIsHidden(doc, { id: ceiling.parentId, type: "ROOM" }, view);
    }
    case "EDGE_BEHAVIOR": {
      const edgeBehavior = docGetTableEntity(doc, handle);
      if (!edgeBehavior) {
        return false;
      }
      return docEntityOrParentIsHidden(doc, { id: edgeBehavior.parentId, type: "ROOM" }, view);
    }
    case "SHAPE":
    case "EDGE":
    case "VERTEX":
    case "ROOF_SIDE": {
      return docEntityOrParentIsHidden(doc, handle.parent, view);
    }
    case "PROP_INSTANCE":
    case "CASEWORK":
    case "ELECTRICAL_DEVICE":
    case "ELECTRICAL_LAYOUT":
    case "ELECTRICAL_RULE":
    case "OPENING":
    case "OPTION":
    case "OPTION_SET":
    case "INSTANCE_OPTION_SET":
    case "OPTION_LOCK":
    case "PALETTE":
    case "SETTINGS":
    case "SETTINGS_SET":
    case "ENTITY_SETTINGS":
    case "OPTION_EFFECT":
    case "OPTION_RULE":
    case "OPTION_TEMPLATE":
    case "OPTION_SET_OVERRIDES":
    case "ROOF":
    case "FOUNDATION":
    case "ROOM_LAYOUT":
    case "ROOM_LAYOUT_INSTANCE":
    case "FACADE_SPAN_STRATUM":
    case "FACADE_SWEEP_STRATUM":
    case "PRESET":
    case "ELEVATION":
    case "SHEET":
    case "DRAWING_REF":
    case "DORMER":
    case "MODIFIER":
    case "EDGE_MODIFIER":
    case "MEASUREMENT":
    case "SKYLIGHT":
    case "GROUP":
    case "VIEW_OVERRIDE":
    case "SELECTOR":
    case "VIEW_SELECTOR":
    case "SITE":
    case "AUTO_DIMENSION": // auto dimensions use a different mechanism to apply visibility
    case "ROOF_OPENING":
    case "VIEW_3D":
    case "ELEVATION_VIEW":
    case "PLAN_VIEW":
    case "COLUMN":
    case "CATALOG_ITEM":
    case "EDIT_MODE":
    case "GENERATED_ENTITY":
    case "PLAN_SET_TEMPLATE":
    case "CONTENT_TEMPLATE":
    case "CONTENT_TEMPLATE_INSTANCE":
    case "CONTENT_REPLICATOR":
    case "VIEW_TEMPLATE":
    case "LOCAL_PARAMETER":
    case "GLOBAL_PARAMETER":
    case "SYSTEM_PARAMETER":
    case "DATA_TABLE":
    case "SITE_SHAPE":
    case "DATA_SEQUENCE":
    case "SECTION_VIEW":
    case "VIEWPORT":
    case "PLAN_SET_TEMPLATE_CONFIG":
    case "LABEL":
    case "VIEW_TEMPLATE_OVERRIDE":
    case "EFFECT":
    case "DATUM":
    case "ANNOTATION":
    case "RULE_BASED_SELECTOR":
    case "RULE_BASED_SELECTOR_INSTANCE":
    case "ASSET":
    case "PROFILE":
    case "RAILING_SETTING":
    case "PROP":
    case "INDETERMINATE_ENTITY_TYPE":
    case "FRAMING_COMPONENT":
    case "FOUNDATION_ASSEMBLY":
    case "FOUNDATION_COMPONENT":
    case "HEADER_ASSEMBLY":
    case "COMPONENT":
    case "COMPONENT_INSTANCE":
    case "FRAME_FLOOR_ASSEMBLY":
    case "SLAB_FLOOR_ASSEMBLY":
    case "SLAB_COMPONENT":
    case "SHEATHING_COMPONENT":
    case "MASONRY_COMPONENT":
    case "CIP_COMPONENT":
    case "COMPOSITE_LEVEL":
    case "FLOOR_PERIMETER_OPENING":
    case "REPORTABLE_UNIT_DESIGNATION":
    case "WALL_PLANE_ASSEMBLY":
    case "WALL_PLANE_FILTER":
    case "WALL_PLANE_RULE":
    case "WALL_PLANE_TRIM":
    case "STAIR_SETTING":
      return false; // can't be hidden
    case "FLOOR_BAY": {
      const floorId = getFloorIdForBay(doc.floors, handle.id);
      if (!floorId) {
        return false;
      }
      return docEntityOrParentIsHidden(doc, { type: "FLOOR", id: floorId }, view);
    }
    case "FLOOR_SPLIT_LINE": {
      const floorId = getFloorIdForSplitLine(doc.floors, handle.id);
      if (!floorId) {
        return false;
      }
      return docEntityOrParentIsHidden(doc, { type: "FLOOR", id: floorId }, view);
    }
    default: {
      assertUnreachable(handle);
    }
  }
}
